import {
  IonLabel,
  IonItem,
  IonSelect,
  IonSelectOption,
  IonAvatar,
  IonAlert,
  IonIcon,
  useIonRouter,
} from '@ionic/react';
import { imageOutline, searchOutline } from 'ionicons/icons';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Layout, { ButtonType } from '@components/Layout';
import List from '@components/List';
import { APIError } from '@custom-types/APIError';
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { useEventsForStaff } from '@pages/Event/queries';
import {
  Event,
  getConfiguration as getConfigurationFn,
  ErrorCode,
  Field,
} from '@pages/Event/service';
import { formatDateRange } from '@services/date-service';
import { generateRoute } from '@services/navigation-service';
import { getLocale } from '@state/system';

const FieldItem = styled(IonItem)`
  padding: 16px 0 10px;
`;

const SearchIcon = styled(IonIcon)`
  width: 100px;
  height: 100px;
`;

const ImageIcon = styled(IonIcon)`
  width: 40px;
  height: 40px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px;
`;

const Avatar = styled(IonAvatar)`
  --border-radius: unset;
`;

const getError = (error: APIError<ErrorCode> | null) => {
  const code = error?.errors?.[0]?.code;

  if (code === ErrorCode.OnsiteAppNotEnabled) {
    return {
      header: t`Unable to Access`,
      message: t`Onsite features are not enabled for this event.`,
    };
  }
  return {
    header: t`Error`,
    message: t`We were unable to process your request at this time. Please try again`,
  };
};

const useManageMobi = () => {
  const { push: pushRoute } = useIonRouter();
  const { presentLoading, dismissLoading } = useLoading();
  const [error, setError] = useState({ header: '', message: '' });

  const onItemClick = useCallback(
    async (event: Event) => {
      try {
        await presentLoading();
        await getConfigurationFn(event.id);
        pushRoute(generateRoute(RoutePath.EventDetail, { eventId: event.id }));
      } catch (err) {
        setError(getError(err as APIError<ErrorCode>));
      } finally {
        await dismissLoading();
      }
    },
    [dismissLoading, generateRoute, presentLoading, pushRoute]
  );

  return { error, setError, onItemClick };
};

const ManageMobi = () => {
  const locale = useSelector(getLocale);
  const [searchField, setSearchSearchField] = useState<Field>(Field.Code);
  const [search, setSearch] = useState('');
  const { error, setError, onItemClick } = useManageMobi();
  const { fetchNextPage, hasNextPage, isFetching, isLoading, remove, data } = useEventsForStaff({
    [searchField]: search,
  });

  const populateItem = useCallback(
    (event: Event) => {
      const dateStr = formatDateRange(event.startDate, event.endDate, locale);

      return (
        <IonItem
          key={event.id}
          // skipcq: JS-0417
          onClick={() => onItemClick(event)}
        >
          <Avatar slot="start">
            {event?.eventIcon?.thumbnailUrl ? (
              <img src={event?.eventIcon?.thumbnailUrl} alt={t`Event Icon`} />
            ) : (
              <ImageIcon icon={imageOutline} />
            )}
          </Avatar>
          <IonLabel>
            <h2>{event.name}</h2>
            <h3>{dateStr}</h3>
          </IonLabel>
        </IonItem>
      );
    },
    [locale, onItemClick]
  );

  return (
    <Layout wrapContent={false} title="ManageMobi" showHeaderBorder buttonType={ButtonType.Back}>
      <>
        <FieldItem>
          <IonSelect
            value={searchField}
            interface="action-sheet"
            // skipcq: JS-0417
            onIonChange={(e: CustomEvent) => setSearchSearchField(e.detail.value as Field)}
            label={t`Search By`}
          >
            <IonSelectOption value={Field.Code}>{t`Event Code`}</IonSelectOption>
            <IonSelectOption value={Field.Id}>{t`Event ID`}</IonSelectOption>
          </IonSelect>
        </FieldItem>
        <List
          loadData={fetchNextPage}
          endOfList={!hasNextPage}
          onSearch={setSearch}
          isLoading={isFetching && isLoading}
          onClear={remove}
        >
          {data && (
            <>
              {data.pages.map(({ data: events }) => events.map(event => populateItem(event)))}
              {data.pages[0].data.length === 0 && (
                <Item>
                  <SearchIcon size="large" icon={searchOutline} color="warning" />
                  <p>{t`No Results Found`}</p>
                </Item>
              )}
            </>
          )}
        </List>
        <IonAlert
          isOpen={!!error.header}
          // skipcq: JS-0417
          onDidDismiss={() => setError({ header: '', message: '' })}
          header={error.header}
          message={error.message}
          buttons={[t`OK`]}
        />
      </>
    </Layout>
  );
};

export default ManageMobi;
